import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { MapPin, ImageIcon, ArrowLeft } from 'lucide-react';
import { Typography, Card, Row, Col, Button, Input, Badge, Modal, Spinner } from 'glintly-ui';
import {
  useProductsByShop,
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct,
  useShopsByOwner,
  useShop,
  useCreateShop,
  useCities,
  useVendorTagSuggestions,
} from '../../../../api';
import { toast } from 'react-toastify';
import styles from '../services.module.css';
import ServiceTagsField from '../ServiceTagsField';
import ShopLocationModal from '../ShopLocationModal';
import { formatShopAddress, shopForProduct } from '../../../../utils/shopAddress';
import CreateServiceModal from './createServiceModal';
import DashboardContainer from '../../../../components/dashboardContainer';

const VendorShopManagement = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateShopModal, setShowCreateShopModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [viewProduct, setViewProduct] = useState(null);
  const [viewMode, setViewMode] = useState('cards');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    search: '',
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    images: [],
    stock: '',
    status: 'ACTIVE',
    sku: '',
    brand: '',
    tags: [],
    variants: [],
    notes: '',
  });

  const [shopFormData, setShopFormData] = useState({
    name: '',
    description: '',
    address: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'India',
      coordinates: {
        latitude: '',
        longitude: '',
      },
    },
    contact: {
      phone: '',
      email: '',
      website: '',
    },
    timings: {
      monday: { open: '09:00', close: '21:00', isOpen: true },
      tuesday: { open: '09:00', close: '21:00', isOpen: true },
      wednesday: { open: '09:00', close: '21:00', isOpen: true },
      thursday: { open: '09:00', close: '21:00', isOpen: true },
      friday: { open: '09:00', close: '21:00', isOpen: true },
      saturday: { open: '09:00', close: '22:00', isOpen: true },
      sunday: { open: '10:00', close: '20:00', isOpen: true },
    },
    services: [],
    amenities: [],
    images: [],
  });
  // Fetch cities from API
  const { data: cities = [] } = useCities();

  // Transform cities data for dropdown options
  const cityOptions = cities.map((city) => ({
    label: city.cityName,
    value: city._id,
  }));

  const userId = user?._id;
  const queryClient = useQueryClient();

  const { data: tagSuggestions } = useVendorTagSuggestions(userId);

  // Get all shops for this vendor
  const {
    data: allShops = [],
    isLoading: shopsLoading,
    error: shopsError,
    refetch: refetchShops,
  } = useShopsByOwner(userId);

  // Filter shops - vendors see all their shops, users only see verified shops
  const shops = allShops.filter((shop) => {
    // For vendors, show all their shops (including unverified)
    // For users, only show verified shops
    return user?.role === 'vendor' || user?.role === 'VENDER' ? true : shop.verified;
  });

  const ownedShop = useMemo(
    () => shops.find((s) => String(s._id) === String(shopId)),
    [shops, shopId]
  );

  const { data: shopDetail, isLoading: shopDetailLoading } = useShop(shopId);

  const selectedShop = useMemo(() => {
    if (!ownedShop || !shopId) return null;
    if (shopDetail && String(shopDetail._id) === String(shopId)) {
      return { ...ownedShop, ...shopDetail };
    }
    return ownedShop;
  }, [ownedShop, shopDetail, shopId]);

  // Create shop hook
  const { mutateAsync: createShop, isPending: isCreatingShop } = useCreateShop();

  // Get products for selected shop
  const {
    data: products = [],
    isLoading: productsLoading,
    error: productsError,
    refetch,
  } = useProductsByShop(shopId && ownedShop ? shopId : undefined);

  const createProductMutation = useCreateProduct();
  const updateProductMutation = useUpdateProduct();
  const deleteProductMutation = useDeleteProduct();

  const filteredProducts = products.filter((product) => {
    const matchesStatus = filters.status === 'all' || product.status === filters.status;
    const matchesCategory = filters.category === 'all' || product.category === filters.category;
    const tagStr = (product.tags || []).join(' ').toLowerCase();
    const matchesSearch =
      filters.search === '' ||
      product.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      product.description?.toLowerCase().includes(filters.search.toLowerCase()) ||
      (product.sku || '').toLowerCase().includes(filters.search.toLowerCase()) ||
      tagStr.includes(filters.search.toLowerCase());

    return matchesStatus && matchesCategory && matchesSearch;
  });

  const stats = {
    total: products.length,
    active: products.filter((p) => p.status === 'ACTIVE').length,
    inactive: products.filter((p) => p.status === 'INACTIVE').length,
    lowStock: products.filter((p) => p.stock < 10).length,
    outOfStock: products.filter((p) => p.stock === 0).length,
  };

  const categories = [
    'Haircut',
    'Beard Trim',
    'Hair Wash',
    'Hair Styling',
    'Hair Color',
    'Facial',
    'Massage',
    'Other',
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    if (!shopId || !selectedShop) {
      toast.error('Shop not found');
      return;
    }
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        shopId: selectedShop._id,
        createdBy: user._id,
      };
      await createProductMutation.mutateAsync(productData);
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error('Create product error:', error);
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
      };
      await updateProductMutation.mutateAsync({
        id: selectedProduct._id,
        data: productData,
      });
      setShowEditModal(false);
      setSelectedProduct(null);
      resetForm();
    } catch (error) {
      console.error('Update product error:', error);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProductMutation.mutateAsync(productId);
      } catch (error) {
        console.error('Delete product error:', error);
      }
    }
  };

  const handleCreateShop = async () => {
    try {
      const shopData = {
        ...shopFormData,
        owner: userId,
        verified: false,
        rating: 0,
        totalReviews: 0,
        status: 'PENDING',
      };

      const res = await createShop(shopData);
      const created = res?.data?.shop ?? res?.data;
      const newId = created?._id;
      toast.success('Shop created successfully! It will be reviewed by admin.');
      setShowCreateShopModal(false);
      setShopFormData({
        name: '',
        description: '',
        address: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'India',
          coordinates: {
            latitude: '',
            longitude: '',
          },
        },
        contact: {
          phone: '',
          email: '',
          website: '',
        },
        timings: {
          monday: { open: '09:00', close: '21:00', isOpen: true },
          tuesday: { open: '09:00', close: '21:00', isOpen: true },
          wednesday: { open: '09:00', close: '21:00', isOpen: true },
          thursday: { open: '09:00', close: '21:00', isOpen: true },
          friday: { open: '09:00', close: '21:00', isOpen: true },
          saturday: { open: '09:00', close: '22:00', isOpen: true },
          sunday: { open: '10:00', close: '20:00', isOpen: true },
        },
        services: [],
        amenities: [],
        images: [],
      });
      await refetchShops();
      if (newId) {
        navigate(`/vendor/shops/${newId}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create shop');
    }
  };

  const handleViewProduct = (product) => {
    setViewProduct(product);
    setShowViewModal(true);
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price?.toString() || '',
      category: product.category || '',
      images: product.images || [],
      stock: product.stock?.toString() || '',
      status: product.status || 'ACTIVE',
      sku: product.sku || '',
      brand: product.brand || '',
      tags: product.tags || [],
      variants: product.variants || [],
      notes: product.notes || '',
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category: '',
      images: [],
      stock: '',
      status: 'ACTIVE',
      sku: '',
      brand: '',
      tags: [],
      variants: [],
      notes: '',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStockColor = (stock) => {
    if (stock === 0) return 'danger';
    if (stock < 10) return 'warning';
    return 'success';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  if (shopsLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" />
        <Typography variant="body1" style={{ marginTop: '16px' }}>
          Loading shops...
        </Typography>
      </div>
    );
  }

  if (shopsError) {
    return (
      <div className={styles.errorContainer}>
        <Typography variant="h6" style={{ color: '#dc2626', marginBottom: '8px' }}>
          Failed to load shops
        </Typography>
        <Typography variant="body2" style={{ color: '#64748b' }}>
          Please try again later or contact support if the problem persists.
        </Typography>
      </div>
    );
  }

  if (!shopId) {
    return <Navigate to="/shops" replace />;
  }

  if (!shopsLoading && !ownedShop) {
    return <Navigate to="/shops" replace />;
  }

  if (shopsLoading || shopDetailLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" />
        <Typography variant="body1" style={{ marginTop: '16px' }}>
          Loading shop…
        </Typography>
      </div>
    );
  }

  if (productsLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" />
        <Typography variant="body1" style={{ marginTop: '16px' }}>
          Loading services...
        </Typography>
      </div>
    );
  }

  if (productsError) {
    toast.error('Failed to load services. Please try again.');
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" />
        <Typography variant="body1" style={{ marginTop: '16px' }}>
          Failed to load services. Please try again.
        </Typography>
      </div>
    );
  }

  return (
    <DashboardContainer
      title="Services Management"
      subtitle="Manage your barber shop services and products"
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate('/shops')}
              style={{ marginBottom: 12 }}
            >
              <ArrowLeft size={16} aria-hidden style={{ marginRight: 6 }} />
              All shops
            </Button>
          </div>
          <div className={styles.shopSelector}>
            <Input.Root name="selected-shop">
              <Input.Control
                type="select"
                value={selectedShop?._id || ''}
                onChange={(e) => {
                  const id = e.target.value;
                  if (id) navigate(`/vendor/shops/${id}`);
                }}
                options={shops.map((shop) => ({
                  label: shop.name,
                  value: shop._id,
                }))}
                placeholder="Select shop"
              />
            </Input.Root>
          </div>
        </div>
        {selectedShop && (
          <div className={styles.selectedShopInfo}>
            <Typography variant="body2" style={{ color: '#64748b' }}>
              Managing: <strong>{selectedShop.name}</strong>
            </Typography>
            {formatShopAddress(selectedShop) ? (
              <Typography
                variant="body2"
                style={{ color: '#475569', marginTop: 8, lineHeight: 1.5 }}
              >
                <MapPin
                  size={14}
                  style={{
                    display: 'inline',
                    verticalAlign: 'text-top',
                    marginRight: 6,
                  }}
                  aria-hidden
                />
                {formatShopAddress(selectedShop)}
              </Typography>
            ) : null}
            <div className={styles.selectedShopActions}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowLocationModal(true)}
              >
                Change location
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Statistics Cards */}
      <Row spacing={3} style={{ marginBottom: '30px' }}>
        <Col xs={12} sm={6} md={2}>
          <Card className={styles.statCard}>
            <Typography variant="h4" className={styles.statNumber}>
              {stats.total}
            </Typography>
            <Typography variant="body2" className={styles.statLabel}>
              Total Services
            </Typography>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={2}>
          <Card className={styles.statCard}>
            <Typography variant="h4" className={styles.statNumber} style={{ color: '#059669' }}>
              {stats.active}
            </Typography>
            <Typography variant="body2" className={styles.statLabel}>
              Active
            </Typography>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={2}>
          <Card className={styles.statCard}>
            <Typography variant="h4" className={styles.statNumber} style={{ color: '#dc2626' }}>
              {stats.inactive}
            </Typography>
            <Typography variant="body2" className={styles.statLabel}>
              Inactive
            </Typography>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={2}>
          <Card className={styles.statCard}>
            <Typography variant="h4" className={styles.statNumber} style={{ color: '#d97706' }}>
              {stats.lowStock}
            </Typography>
            <Typography variant="body2" className={styles.statLabel}>
              Low Stock
            </Typography>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={2}>
          <Card className={styles.statCard}>
            <Typography variant="h4" className={styles.statNumber} style={{ color: '#dc2626' }}>
              {stats.outOfStock}
            </Typography>
            <Typography variant="body2" className={styles.statLabel}>
              Out of Stock
            </Typography>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={2}>
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            className={styles.addButton}
          >
            Add Service
          </Button>
        </Col>
      </Row>

      {/* Filters */}
      <Card className={styles.filtersCard}>
        <Row spacing={3} align="center">
          <Col xs={12} md={4}>
            <Input.Root name="services-filter-search">
              <Input.Control
                placeholder="Search services..."
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                className={styles.searchInput}
              />
            </Input.Root>
          </Col>
          <Col xs={12} md={3}>
            <Input.Root name="services-filter-status">
              <Input.Control
                type="select"
                value={filters.status}
                onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                options={[
                  { label: 'All Status', value: 'all' },
                  { label: 'Active', value: 'ACTIVE' },
                  { label: 'Inactive', value: 'INACTIVE' },
                ]}
              />
            </Input.Root>
          </Col>
          <Col xs={12} md={3}>
            <Input.Root name="services-filter-category">
              <Input.Control
                type="select"
                value={filters.category}
                onChange={(e) => setFilters((prev) => ({ ...prev, category: e.target.value }))}
                options={[
                  { label: 'All Categories', value: 'all' },
                  ...categories.map((cat) => ({ label: cat, value: cat })),
                ]}
              />
            </Input.Root>
          </Col>
          <Col xs={12} md={2}>
            <Button variant="outline" onClick={() => refetch()} className={styles.refreshBtn}>
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Services — table or cards */}
      <Card className={styles.tableCard}>
        <div className={styles.viewToggleRow}>
          <Typography variant="h6" style={{ margin: 0 }}>
            Services ({filteredProducts.length})
          </Typography>
          <div className={styles.viewToggle} role="group" aria-label="View mode">
            <button
              type="button"
              className={`${styles.viewToggleBtn} ${viewMode === 'cards' ? styles.viewToggleBtnActive : ''}`}
              onClick={() => setViewMode('cards')}
            >
              Cards
            </button>
            <button
              type="button"
              className={`${styles.viewToggleBtn} ${viewMode === 'table' ? styles.viewToggleBtnActive : ''}`}
              onClick={() => setViewMode('table')}
            >
              Table
            </button>
          </div>
        </div>

        {filteredProducts.length === 0 ? (
          <div className={styles.emptyState}>
            <Typography variant="h6" style={{ color: '#64748b', marginBottom: '8px' }}>
              No services found
            </Typography>
            <Typography variant="body2" style={{ color: '#9ca3af' }}>
              {filters.search || filters.status !== 'all' || filters.category !== 'all'
                ? 'Try adjusting your filters'
                : 'No services have been added yet'}
            </Typography>
          </div>
        ) : viewMode === 'cards' ? (
          <div className={styles.servicesCardGrid}>
            {filteredProducts.map((product) => {
              const shopCtx = shopForProduct(product, selectedShop);
              const addr = formatShopAddress(shopCtx);
              return (
                <div key={product._id} className={styles.serviceCard}>
                  <div className={styles.serviceCardMedia}>
                    {product.images?.[0] ? (
                      <img src={product.images[0]} alt="" />
                    ) : (
                      <div className={styles.serviceCardMediaPlaceholder}>
                        <ImageIcon size={40} strokeWidth={1.25} aria-hidden />
                      </div>
                    )}
                  </div>
                  <div className={styles.serviceCardBody}>
                    <h3 className={styles.serviceCardTitle}>{product.name}</h3>
                    <div className={styles.serviceCardMeta}>
                      <span className={styles.serviceCardPrice}>
                        {formatCurrency(product.price)}
                      </span>
                      <Badge
                        variant={getStatusColor(product.status)}
                        className={styles.statusBadge}
                      >
                        {product.status}
                      </Badge>
                      <Badge variant={getStockColor(product.stock)} className={styles.stockBadge}>
                        Stock {product.stock}
                      </Badge>
                    </div>
                    {product.category ? (
                      <Typography variant="caption" style={{ color: '#64748b' }}>
                        {product.category}
                      </Typography>
                    ) : null}
                    {addr ? (
                      <div className={styles.serviceCardLocation}>
                        <MapPin size={14} aria-hidden />
                        <span title={addr}>{addr}</span>
                      </div>
                    ) : null}
                    {product.description ? (
                      <p className={styles.serviceCardDesc}>{product.description}</p>
                    ) : null}
                    {(product.tags || []).length > 0 ? (
                      <div className={styles.serviceCardTags}>
                        {(product.tags || []).slice(0, 4).map((t) => (
                          <span key={t} className={styles.serviceCardTag}>
                            {t}
                          </span>
                        ))}
                        {(product.tags || []).length > 4 ? (
                          <span className={styles.serviceCardTag}>
                            +{(product.tags || []).length - 4}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                    <div className={styles.serviceCardActions}>
                      <Button
                        size="sm"
                        variant="primary"
                        type="button"
                        onClick={() => handleViewProduct(product)}
                      >
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        type="button"
                        onClick={() => handleEditProduct(product)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        type="button"
                        onClick={() => handleDeleteProduct(product._id)}
                        disabled={deleteProductMutation.isPending}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.servicesTable}>
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Location</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th>SKU</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => {
                  const shopCtx = shopForProduct(product, selectedShop);
                  const addr = formatShopAddress(shopCtx);
                  return (
                    <tr key={product._id}>
                      <td>
                        <div>
                          <Typography variant="body2" style={{ fontWeight: '600' }}>
                            {product.name}
                          </Typography>
                          {product.description && (
                            <Typography variant="caption" style={{ color: '#64748b' }}>
                              {product.description.length > 50
                                ? `${product.description.substring(0, 50)}…`
                                : product.description}
                            </Typography>
                          )}
                        </div>
                      </td>
                      <td className={styles.tableLocationCell}>
                        {shopCtx?.name ? (
                          <strong style={{ display: 'block', marginBottom: 4 }}>
                            {shopCtx.name}
                          </strong>
                        ) : null}
                        {addr || '—'}
                      </td>
                      <td>
                        <Typography variant="body2">{product.category}</Typography>
                      </td>
                      <td>
                        <Typography variant="body2" style={{ fontWeight: '600' }}>
                          {formatCurrency(product.price)}
                        </Typography>
                      </td>
                      <td>
                        <Badge variant={getStockColor(product.stock)} className={styles.stockBadge}>
                          {product.stock}
                        </Badge>
                      </td>
                      <td>
                        <Badge
                          variant={getStatusColor(product.status)}
                          className={styles.statusBadge}
                        >
                          {product.status}
                        </Badge>
                      </td>
                      <td>
                        <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                          {product.sku}
                        </Typography>
                      </td>
                      <td>
                        <div className={styles.actionButtons}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewProduct(product)}
                          >
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditProduct(product)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleDeleteProduct(product._id)}
                            disabled={deleteProductMutation.isPending}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ShopLocationModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        shop={selectedShop}
        cityOptions={cityOptions}
        onSaved={(updatedShop) => {
          queryClient.invalidateQueries({
            queryKey: ['shops', 'owner', userId],
          });
          queryClient.invalidateQueries({
            queryKey: ['shops', updatedShop?._id],
          });
        }}
      />

      {/* View service detail */}
      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setViewProduct(null);
        }}
        size="lg"
      >
        <Modal.Header>Service details</Modal.Header>
        <Modal.Body>
          {viewProduct && (
            <div>
              <Typography variant="h5" style={{ marginBottom: '8px' }}>
                {viewProduct.name}
              </Typography>
              {(() => {
                const shopCtx = shopForProduct(viewProduct, selectedShop);
                const addr = formatShopAddress(shopCtx);
                return (
                  <>
                    {shopCtx ? (
                      <div className={styles.viewModalSection}>
                        <h4>Shop &amp; location</h4>
                        <p style={{ fontWeight: 600 }}>{shopCtx.name}</p>
                        {shopCtx.verified !== undefined ? (
                          <Badge
                            variant={shopCtx.verified ? 'success' : 'warning'}
                            style={{ marginBottom: 8 }}
                          >
                            {shopCtx.verified ? 'Verified' : 'Pending verification'}
                          </Badge>
                        ) : null}
                        {addr ? (
                          <p
                            style={{
                              display: 'flex',
                              gap: 8,
                              alignItems: 'flex-start',
                            }}
                          >
                            <MapPin size={18} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden />
                            <span>{addr}</span>
                          </p>
                        ) : (
                          <p style={{ color: '#94a3b8' }}>No address on file for this shop.</p>
                        )}
                        {shopCtx.rating != null ? (
                          <p style={{ fontSize: '0.875rem', color: '#64748b' }}>
                            Rating: {shopCtx.rating}
                            {shopCtx.totalReviews != null
                              ? ` (${shopCtx.totalReviews} reviews)`
                              : ''}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                );
              })()}

              <div className={styles.viewModalGrid}>
                <div className={styles.viewModalStat}>
                  <div className={styles.viewModalStatLabel}>Price</div>
                  <div className={styles.viewModalStatValue}>
                    {formatCurrency(viewProduct.price)}
                  </div>
                </div>
                <div className={styles.viewModalStat}>
                  <div className={styles.viewModalStatLabel}>Stock</div>
                  <div className={styles.viewModalStatValue}>{viewProduct.stock}</div>
                </div>
                <div className={styles.viewModalStat}>
                  <div className={styles.viewModalStatLabel}>SKU</div>
                  <div
                    className={styles.viewModalStatValue}
                    style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                  >
                    {viewProduct.sku}
                  </div>
                </div>
                <div className={styles.viewModalStat}>
                  <div className={styles.viewModalStatLabel}>Category</div>
                  <div className={styles.viewModalStatValue}>{viewProduct.category || '—'}</div>
                </div>
                <div className={styles.viewModalStat}>
                  <div className={styles.viewModalStatLabel}>Status</div>
                  <div className={styles.viewModalStatValue}>
                    <Badge variant={getStatusColor(viewProduct.status)}>{viewProduct.status}</Badge>
                  </div>
                </div>
                <div className={styles.viewModalStat}>
                  <div className={styles.viewModalStatLabel}>Brand</div>
                  <div className={styles.viewModalStatValue}>{viewProduct.brand || '—'}</div>
                </div>
              </div>

              {viewProduct.duration != null && viewProduct.duration !== '' ? (
                <div className={styles.viewModalSection}>
                  <h4>Duration</h4>
                  <p>{String(viewProduct.duration)}</p>
                </div>
              ) : null}

              {viewProduct.description ? (
                <div className={styles.viewModalSection}>
                  <h4>Description</h4>
                  <p>{viewProduct.description}</p>
                </div>
              ) : null}

              {(viewProduct.tags || []).length > 0 ? (
                <div className={styles.viewModalSection}>
                  <h4>Tags</h4>
                  <div className={styles.serviceCardTags}>
                    {(viewProduct.tags || []).map((t) => (
                      <span key={t} className={styles.serviceCardTag}>
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              {Array.isArray(viewProduct.variants) && viewProduct.variants.length > 0 ? (
                <div className={styles.viewModalSection}>
                  <h4>Variants</h4>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                    {viewProduct.variants.map((v, i) => (
                      <li key={i}>
                        <strong>{v.name}:</strong> {v.value}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {viewProduct.notes ? (
                <div className={styles.viewModalSection}>
                  <h4>Internal notes</h4>
                  <p>{viewProduct.notes}</p>
                </div>
              ) : null}

              {(viewProduct.images || []).length > 0 ? (
                <div className={styles.viewModalSection}>
                  <h4>Images</h4>
                  <div className={styles.viewModalImageRow}>
                    {(viewProduct.images || []).map((src, i) => (
                      <img key={i} src={src} alt="" />
                    ))}
                  </div>
                </div>
              ) : null}

              {(viewProduct.createdAt || viewProduct.updatedAt) && (
                <div className={styles.viewModalSection}>
                  <h4>Record</h4>
                  <p style={{ fontSize: '0.875rem', color: '#64748b' }}>
                    {viewProduct.createdAt
                      ? `Created: ${new Date(viewProduct.createdAt).toLocaleString()}`
                      : ''}
                    {viewProduct.createdAt && viewProduct.updatedAt ? ' · ' : ''}
                    {viewProduct.updatedAt
                      ? `Updated: ${new Date(viewProduct.updatedAt).toLocaleString()}`
                      : ''}
                  </p>
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  justifyContent: 'flex-end',
                  marginTop: 16,
                }}
              >
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    setShowViewModal(false);
                    handleEditProduct(viewProduct);
                  }}
                >
                  Edit service
                </Button>
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => {
                    setShowViewModal(false);
                    setViewProduct(null);
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Create Service Modal */}
      <CreateServiceModal
        showCreateModal={showCreateModal}
        setShowCreateModal={setShowCreateModal}
        userId={userId}
        resetForm={resetForm}
        handleCreateProduct={handleCreateProduct}
        handleInputChange={handleInputChange}
        formData={formData}
      />

      {/* Edit Service Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedProduct(null);
          resetForm();
        }}
        size="lg"
      >
        <Modal.Header>Edit Service</Modal.Header>
        <Modal.Body>
          <div>
            <form onSubmit={handleUpdateProduct}>
              <div className={styles.serviceFormGrid}>
                <div className={`${styles.serviceFormField} ${styles.serviceFormHalf}`}>
                  <Input.Root name="name" required>
                    <Input.Label>Service Name</Input.Label>
                    <Input.Control
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter service name"
                    />
                  </Input.Root>
                </div>
                <div className={`${styles.serviceFormField} ${styles.serviceFormHalf}`}>
                  <Input.Root name="sku" required>
                    <Input.Label>SKU</Input.Label>
                    <Input.Control
                      value={formData.sku}
                      onChange={handleInputChange}
                      placeholder="Enter SKU"
                    />
                  </Input.Root>
                </div>
                <div className={styles.serviceFormField}>
                  <Input.Root name="description">
                    <Input.Label>Description</Input.Label>
                    <Input.Control
                      type="textarea"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Enter service description"
                      rows={3}
                    />
                  </Input.Root>
                </div>
                <div className={`${styles.serviceFormField} ${styles.serviceFormHalf}`}>
                  <Input.Root name="price" required>
                    <Input.Label>Price (₹)</Input.Label>
                    <Input.Control
                      type="number"
                      value={formData.price}
                      onChange={handleInputChange}
                      placeholder="Enter price"
                    />
                  </Input.Root>
                </div>
                <div className={`${styles.serviceFormField} ${styles.serviceFormHalf}`}>
                  <Input.Root name="stock" required>
                    <Input.Label>Stock</Input.Label>
                    <Input.Control
                      type="number"
                      value={formData.stock}
                      onChange={handleInputChange}
                      placeholder="Enter stock quantity"
                    />
                  </Input.Root>
                </div>
                <div className={`${styles.serviceFormField} ${styles.serviceFormHalf}`}>
                  <Input.Root name="category" required>
                    <Input.Label>Category</Input.Label>
                    <Input.Control
                      type="select"
                      value={formData.category}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          category: e.target.value,
                        }))
                      }
                      options={categories.map((cat) => ({
                        label: cat,
                        value: cat,
                      }))}
                      placeholder="Select category"
                    />
                  </Input.Root>
                </div>
                <div className={`${styles.serviceFormField} ${styles.serviceFormHalf}`}>
                  <Input.Root name="status">
                    <Input.Label>Status</Input.Label>
                    <Input.Control
                      type="select"
                      value={formData.status}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                      options={[
                        { label: 'Active', value: 'ACTIVE' },
                        { label: 'Inactive', value: 'INACTIVE' },
                      ]}
                    />
                  </Input.Root>
                </div>
                <div className={`${styles.serviceFormField} ${styles.serviceFormHalf}`}>
                  <Input.Root name="brand">
                    <Input.Label>Brand</Input.Label>
                    <Input.Control
                      value={formData.brand}
                      onChange={handleInputChange}
                      placeholder="Enter brand name"
                    />
                  </Input.Root>
                </div>
                <ServiceTagsField
                  formData={formData}
                  setFormData={setFormData}
                  suggestionData={tagSuggestions}
                />
                <div className={styles.serviceFormField}>
                  <Input.Root name="notes">
                    <Input.Label>Notes</Input.Label>
                    <Input.Control
                      type="textarea"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Enter any additional notes"
                      rows={3}
                    />
                  </Input.Root>
                </div>
              </div>

              <div className={styles.modalActions}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedProduct(null);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={updateProductMutation.isPending}>
                  {updateProductMutation.isPending ? 'Updating...' : 'Update Service'}
                </Button>
              </div>
            </form>
          </div>
        </Modal.Body>
      </Modal>

      {/* Create Shop Modal (when managing services) */}
      <Modal isOpen={showCreateShopModal} onClose={() => setShowCreateShopModal(false)} size="lg">
        <Modal.Header>Create New Shop</Modal.Header>
        <Modal.Body>
          <div>
            <div className={styles.formGroup}>
              <Input.Root name="name" required>
                <Input.Label>Shop Name</Input.Label>
                <Input.Control
                  type="text"
                  value={shopFormData.name}
                  onChange={(e) =>
                    setShopFormData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder="Enter shop name"
                />
              </Input.Root>
            </div>

            <div className={styles.formGroup}>
              <Input.Root name="description">
                <Input.Label>Description</Input.Label>
                <Input.Control
                  type="textarea"
                  value={shopFormData.description}
                  onChange={(e) =>
                    setShopFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Describe your shop"
                  rows={3}
                />
              </Input.Root>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input.Root name="street" required>
                  <Input.Label>Street Address</Input.Label>
                  <Input.Control
                    type="text"
                    value={shopFormData.address.street}
                    onChange={(e) =>
                      setShopFormData((prev) => ({
                        ...prev,
                        address: { ...prev.address, street: e.target.value },
                      }))
                    }
                    placeholder="Enter street address"
                  />
                </Input.Root>
              </div>
              <div className={styles.formGroup}>
                <Input.Root name="city" required>
                  <Input.Label>City</Input.Label>
                  <Input.Control
                    type="select"
                    value={shopFormData.address.city}
                    options={cityOptions}
                    onChange={(e) =>
                      setShopFormData((prev) => ({
                        ...prev,
                        address: { ...prev.address, city: e.target.value },
                      }))
                    }
                    placeholder="Enter city"
                  />
                </Input.Root>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input.Root name="state" required>
                  <Input.Label>State</Input.Label>
                  <Input.Control
                    type="text"
                    value={shopFormData.address.state}
                    onChange={(e) =>
                      setShopFormData((prev) => ({
                        ...prev,
                        address: { ...prev.address, state: e.target.value },
                      }))
                    }
                    placeholder="Enter state"
                  />
                </Input.Root>
              </div>
              <div className={styles.formGroup}>
                <Input.Root name="postalCode" required>
                  <Input.Label>Postal Code</Input.Label>
                  <Input.Control
                    type="text"
                    value={shopFormData.address.postalCode}
                    onChange={(e) =>
                      setShopFormData((prev) => ({
                        ...prev,
                        address: {
                          ...prev.address,
                          postalCode: e.target.value,
                        },
                      }))
                    }
                    placeholder="Enter postal code"
                  />
                </Input.Root>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input.Root name="phone" required>
                  <Input.Label>Phone Number</Input.Label>
                  <Input.Control
                    type="text"
                    value={shopFormData.contact.phone}
                    onChange={(e) =>
                      setShopFormData((prev) => ({
                        ...prev,
                        contact: { ...prev.contact, phone: e.target.value },
                      }))
                    }
                    placeholder="Enter phone number"
                  />
                </Input.Root>
              </div>
              <div className={styles.formGroup}>
                <Input.Root name="email" required>
                  <Input.Label>Email</Input.Label>
                  <Input.Control
                    type="email"
                    value={shopFormData.contact.email}
                    onChange={(e) =>
                      setShopFormData((prev) => ({
                        ...prev,
                        contact: { ...prev.contact, email: e.target.value },
                      }))
                    }
                    placeholder="Enter email"
                  />
                </Input.Root>
              </div>
            </div>

            <div className={styles.formGroup}>
              <Input.Root name="website">
                <Input.Label>Website (Optional)</Input.Label>
                <Input.Control
                  type="url"
                  value={shopFormData.contact.website}
                  onChange={(e) =>
                    setShopFormData((prev) => ({
                      ...prev,
                      contact: { ...prev.contact, website: e.target.value },
                    }))
                  }
                  placeholder="Enter website URL"
                />
              </Input.Root>
            </div>

            <div className={styles.modalActions}>
              <Button
                variant="outline"
                onClick={() => setShowCreateShopModal(false)}
                disabled={isCreatingShop}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateShop}
                disabled={
                  isCreatingShop ||
                  !shopFormData.name ||
                  !shopFormData.address.street ||
                  !shopFormData.address.city ||
                  !shopFormData.contact.phone
                }
              >
                {isCreatingShop ? 'Creating...' : 'Create Shop'}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </DashboardContainer>
  );
};

export default VendorShopManagement;
